import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { AlertTriangle, FileText, Timer } from 'lucide-react';
import type { ExamInfo } from 'shared';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { Button } from '@/components/ui/button';
import { apiFetch } from '@/lib/api';
import { translateApiError } from '@/lib/api-error';

export function ExamLandingPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { token = '' } = useParams();

  const exam = useQuery({
    queryKey: ['exam', token],
    queryFn: () => apiFetch<ExamInfo>(`/public/exam/${encodeURIComponent(token)}`),
    retry: false,
  });

  return (
    <main className="mx-auto flex min-h-dvh w-full max-w-xl flex-col gap-5 px-5 py-8">
      <header className="flex items-start justify-between gap-4">
        <p className="text-sm font-medium text-slate-500">{t('app.org')}</p>
        <LanguageSwitcher />
      </header>

      {exam.isPending && <p className="text-slate-600">{t('common.loading')}</p>}

      {exam.isError && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-5">
          <p className="flex items-start gap-2 font-medium text-red-800">
            <AlertTriangle aria-hidden className="mt-0.5 h-5 w-5 shrink-0" />
            {translateApiError(exam.error, t)}
          </p>
        </div>
      )}

      {exam.isSuccess && (
        <>
          <div>
            <p className="text-sm font-medium text-slate-500">{exam.data.courseTitle}</p>
            <h1 className="mt-1 text-2xl font-semibold text-slate-900">{exam.data.title}</h1>
          </div>

          <dl className="grid grid-cols-2 gap-3">
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <dt className="flex items-center gap-1.5 text-sm text-slate-600">
                <FileText aria-hidden className="h-4 w-4" />
                {t('landing.questionCount')}
              </dt>
              <dd className="mt-1 text-lg font-semibold text-slate-900">
                {t('landing.questionCountValue', { count: exam.data.questionCount })}
              </dd>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <dt className="flex items-center gap-1.5 text-sm text-slate-600">
                <Timer aria-hidden className="h-4 w-4" />
                {t('landing.duration')}
              </dt>
              <dd className="mt-1 text-lg font-semibold text-slate-900">
                {exam.data.durationMinutes
                  ? t('landing.durationValue', { count: exam.data.durationMinutes })
                  : t('landing.noTimeLimit')}
              </dd>
            </div>
          </dl>

          <section className="rounded-xl border border-slate-200 bg-white p-5">
            <h2 className="text-base font-semibold text-slate-900">{t('landing.instructionsTitle')}</h2>
            <p className="mt-2 whitespace-pre-wrap leading-relaxed text-slate-700">
              {exam.data.instructions ?? t('landing.defaultInstructions')}
            </p>
          </section>

          {exam.data.kind === 'posttest' && (
            <p className="flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 p-4 font-medium text-amber-900">
              <AlertTriangle aria-hidden className="mt-0.5 h-5 w-5 shrink-0" />
              {t('landing.posttestWarning')}
            </p>
          )}

          <Button size="lg" onClick={() => void navigate(`/t/${token}/register`)}>
            {t('landing.start')}
          </Button>
        </>
      )}
    </main>
  );
}
